import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { cardsApi } from "@/lib/api";

const EMPTY = {
  first_name: "", last_name: "", full_name: "", initials: "",
  job_title: "", department: "", company: "Elite Digital d.o.o.",
  phone: "", work_phone: "", cell_phone: "",
  email: "", website: "https://www.elite-digital.hr",
  address: "", city: "", zip: "", country: "Croatia",
  linkedin: "", facebook: "", instagram: "",
  profile_image: "", header_image: "",
  header_color: "#0B1F38", primary_color: "#18BFFF",
  slug: "", active: false, public_description: "",
};

export default function CardEditor({ open, onOpenChange, card, onSaved }) {
  const [data, setData] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  let saveButtonLabel;
  if (saving) {
    saveButtonLabel = "Saving...";
  } else if (card) {
    saveButtonLabel = "Save changes";
  } else {
    saveButtonLabel = "Create card";
  }

  useEffect(() => {
    setData(card ? { ...EMPTY, ...card } : EMPTY);
  }, [card, open]);

  const set = (k, v) => setData((d) => {
    const next = { ...d, [k]: v };
    // Auto-compute full_name/initials if user hasn't customized
    if (k === "first_name" || k === "last_name") {
      const fn = k === "first_name" ? v : next.first_name;
      const ln = k === "last_name" ? v : next.last_name;
      const computed = `${fn} ${ln}`.trim();
      if (!d.full_name || d.full_name === `${d.first_name} ${d.last_name}`.trim()) {
        next.full_name = computed;
      }
      const inits = ((fn[0] || "") + (ln[0] || "")).toUpperCase();
      if (!d.initials || d.initials === ((d.first_name[0] || "") + (d.last_name[0] || "")).toUpperCase()) {
        next.initials = inits;
      }
    }
    return next;
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!data.slug || !data.slug.trim()) {
      toast.error("Slug is required");
      return;
    }
    if (!data.full_name && !data.first_name && !data.last_name) {
      toast.error("Name is required");
      return;
    }
    setSaving(true);
    try {
      if (card && card.id) {
        await cardsApi.update(card.id, data);
      } else {
        await cardsApi.create(data);
      }
      onSaved?.();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to save";
      toast.error(String(msg));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#101B28] border-[#1E2D40] text-white max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="card-editor">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">
            {card ? "Edit card" : "Create new card"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Tabs defaultValue="identity">
            <TabsList className="bg-[#0B131E] border border-[#1E2D40]">
              <TabsTrigger value="identity" data-testid="tab-identity">Identity</TabsTrigger>
              <TabsTrigger value="contact" data-testid="tab-contact">Contact</TabsTrigger>
              <TabsTrigger value="social" data-testid="tab-social">Social</TabsTrigger>
              <TabsTrigger value="visual" data-testid="tab-visual">Visual</TabsTrigger>
              <TabsTrigger value="publish" data-testid="tab-publish">Publish</TabsTrigger>
            </TabsList>

            <TabsContent value="identity" className="space-y-4 pt-4">
              <Grid2>
                <Field label="First name">
                  <Input data-testid="input-first_name" value={data.first_name} onChange={(e) => set("first_name", e.target.value)} className={inputCls} />
                </Field>
                <Field label="Last name">
                  <Input data-testid="input-last_name" value={data.last_name} onChange={(e) => set("last_name", e.target.value)} className={inputCls} />
                </Field>
              </Grid2>
              <Grid2>
                <Field label="Full name">
                  <Input data-testid="input-full_name" value={data.full_name} onChange={(e) => set("full_name", e.target.value)} className={inputCls} />
                </Field>
                <Field label="Initials">
                  <Input data-testid="input-initials" value={data.initials} onChange={(e) => set("initials", e.target.value)} maxLength={4} className={inputCls} />
                </Field>
              </Grid2>
              <Grid2>
                <Field label="Job title">
                  <Input data-testid="input-job_title" value={data.job_title} onChange={(e) => set("job_title", e.target.value)} className={inputCls} />
                </Field>
                <Field label="Department">
                  <Input data-testid="input-department" value={data.department} onChange={(e) => set("department", e.target.value)} className={inputCls} />
                </Field>
              </Grid2>
              <Field label="Company">
                <Input data-testid="input-company" value={data.company} onChange={(e) => set("company", e.target.value)} className={inputCls} />
              </Field>
              <Field label="Public description">
                <Textarea data-testid="input-public_description" value={data.public_description} onChange={(e) => set("public_description", e.target.value)} rows={2} className={inputCls} />
              </Field>
            </TabsContent>

            <TabsContent value="contact" className="space-y-4 pt-4">
              <Grid2>
                <Field label="Email">
                  <Input data-testid="input-email" type="email" value={data.email} onChange={(e) => set("email", e.target.value)} className={inputCls} />
                </Field>
                <Field label="Website">
                  <Input data-testid="input-website" value={data.website} onChange={(e) => set("website", e.target.value)} className={inputCls} />
                </Field>
              </Grid2>
              <Grid2>
                <Field label="Mobile / Cell">
                  <Input data-testid="input-cell_phone" value={data.cell_phone} onChange={(e) => set("cell_phone", e.target.value)} className={inputCls} />
                </Field>
                <Field label="Work phone">
                  <Input data-testid="input-work_phone" value={data.work_phone} onChange={(e) => set("work_phone", e.target.value)} className={inputCls} />
                </Field>
              </Grid2>
              <Field label="Phone (general)">
                <Input data-testid="input-phone" value={data.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls} />
              </Field>
              <Field label="Address">
                <Input data-testid="input-address" value={data.address} onChange={(e) => set("address", e.target.value)} className={inputCls} />
              </Field>
              <Grid2>
                <Field label="City">
                  <Input data-testid="input-city" value={data.city} onChange={(e) => set("city", e.target.value)} className={inputCls} />
                </Field>
                <Field label="ZIP">
                  <Input data-testid="input-zip" value={data.zip} onChange={(e) => set("zip", e.target.value)} className={inputCls} />
                </Field>
              </Grid2>
              <Field label="Country">
                <Input data-testid="input-country" value={data.country} onChange={(e) => set("country", e.target.value)} className={inputCls} />
              </Field>
            </TabsContent>

            <TabsContent value="social" className="space-y-4 pt-4">
              <Field label="LinkedIn URL">
                <Input data-testid="input-linkedin" value={data.linkedin} onChange={(e) => set("linkedin", e.target.value)} className={inputCls} placeholder="https://linkedin.com/in/..." />
              </Field>
              <Field label="Facebook URL">
                <Input data-testid="input-facebook" value={data.facebook} onChange={(e) => set("facebook", e.target.value)} className={inputCls} placeholder="https://facebook.com/..." />
              </Field>
              <Field label="Instagram URL">
                <Input data-testid="input-instagram" value={data.instagram} onChange={(e) => set("instagram", e.target.value)} className={inputCls} placeholder="https://instagram.com/..." />
              </Field>
            </TabsContent>

            <TabsContent value="visual" className="space-y-4 pt-4">
              <Field label="Profile image URL">
                <Input data-testid="input-profile_image" value={data.profile_image} onChange={(e) => set("profile_image", e.target.value)} className={inputCls} />
              </Field>
              <Field label="Header image URL">
                <Input data-testid="input-header_image" value={data.header_image} onChange={(e) => set("header_image", e.target.value)} className={inputCls} />
              </Field>
              <Grid2>
                <Field label="Primary color">
                  <div className="flex gap-2">
                    <Input data-testid="input-primary_color" value={data.primary_color} onChange={(e) => set("primary_color", e.target.value)} className={inputCls} />
                    <div className="w-10 h-10 rounded-md border border-[#1E2D40]" style={{ background: data.primary_color }} />
                  </div>
                </Field>
                <Field label="Header color">
                  <div className="flex gap-2">
                    <Input data-testid="input-header_color" value={data.header_color} onChange={(e) => set("header_color", e.target.value)} className={inputCls} />
                    <div className="w-10 h-10 rounded-md border border-[#1E2D40]" style={{ background: data.header_color }} />
                  </div>
                </Field>
              </Grid2>
            </TabsContent>

            <TabsContent value="publish" className="space-y-4 pt-4">
              <Field label="Slug (public URL path)">
                <Input data-testid="input-slug" value={data.slug} onChange={(e) => set("slug", e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"))} className={inputCls} placeholder="e.g. john-doe" />
              </Field>
              <div className="text-xs text-[#94A3B8]">
                Public URL: <code className="text-[#18BFFF]">/card/{data.slug || "your-slug"}</code>
              </div>
              <div className="flex items-center justify-between rounded-lg border border-[#1E2D40] bg-[#0B131E] px-4 py-3">
                <div>
                  <div className="font-medium text-white">Card is active</div>
                  <div className="text-xs text-[#94A3B8]">When active, the public link is accessible.</div>
                </div>
                <Switch
                  data-testid="switch-active"
                  checked={data.active}
                  onCheckedChange={(v) => set("active", v)}
                  className="data-[state=checked]:bg-[#18BFFF]"
                />
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="editor-cancel"
                    className="border-[#1E2D40] bg-transparent text-white hover:bg-[#162436] hover:text-white">
              Cancel
            </Button>
            <Button type="submit" disabled={saving} data-testid="editor-save"
                    className="bg-[#18BFFF] text-[#07111D] hover:bg-[#4DD0FF]">
              {saveButtonLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const inputCls = "bg-[#0B131E] border-[#1E2D40] text-white placeholder:text-[#4A5A73] focus-visible:ring-[#18BFFF] focus-visible:border-[#18BFFF]";

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-[0.15em] text-[#94A3B8]">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function Grid2({ children }) {
  return <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>;
}
